import React, { useEffect, useMemo, useState } from 'react';
import { NodeProps } from '@xyflow/react';
import useLocale from 'next-translate/useTranslation';
import clsxm from '@/lib/clsxm';
import { BaseNode, createInputHandle, createOutputHandle } from './BaseNode';
import { NodeExecutionStatus } from 'types';

import { useWorkflowStore } from '@/lib/workflows/store';
import { useIsMobile } from '@/hooks/useIsMobile';

import Button from '@/components/buttons/Button';
import { PlayIcon, RefreshIcon } from '@heroicons/react/outline';
import { NodeActionsDropdown } from './NodeActionsDropdown';

import { SimpleTooltip } from '@/components/ui/tooltip';

interface VideoMergeNodeProps extends NodeProps {
  onRunNode?: (nodeId: string) => void;
  nodeId?: string;
}

export function VideoMergeNode({
  id,
  data,
  selected,
  onRunNode,
  nodeId,
}: VideoMergeNodeProps) {
  const { t } = useLocale('common');
  const isMobile = useIsMobile();
  const allEdges = useWorkflowStore((state) => state.edges);
  const allNodes = useWorkflowStore((state) => state.nodes);
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);

  const rawResult = (data as any)?.result;
  const videoUrl: string | undefined =
    typeof rawResult === 'string'
      ? rawResult
      : rawResult && typeof rawResult === 'object'
        ? rawResult.videoUrl ||
          rawResult.video_url ||
          rawResult.video?.url ||
          rawResult.url
        : undefined;
  const [showSettings, setShowSettings] = useState<boolean>(!videoUrl);
  const [isLandscape, setIsLandscape] = useState(false);

  const rawError = (data as any)?.error;
  const errorText =
    typeof rawError === 'string'
      ? rawError.trim()
      : rawError != null
        ? String(rawError).trim()
        : '';

  const config = ((data as any)?.config || {}) as {
    videoOrder?: string[];
  };

  const isRunning =
    ((data as any)?.status as NodeExecutionStatus) === 'running';

  useEffect(() => {
    if (videoUrl) {
      setShowSettings(false);
    }
    setIsLandscape(false);
  }, [videoUrl]);

  // Hide result if running or idle (cleared)
  const shouldShowResult =
    !!videoUrl && !isRunning && (data as any)?.status === 'success';

  const connectedVideos = useMemo(() => {
    const incoming = allEdges.filter(
      (e) => e.target === id && e.targetHandle === 'video',
    );

    const bySource = new Map<
      string,
      { id: string; label: string; hasUrl: boolean }
    >();

    incoming.forEach((edge) => {
      const sourceNode = allNodes.find((n) => n.id === edge.source);
      if (!sourceNode) return;
      const label =
        (sourceNode.data?.label as string) ||
        (sourceNode.type as string) ||
        edge.source;

      // We don't know the exact URL here, just track presence
      bySource.set(edge.source, {
        id: edge.source,
        label,
        hasUrl: true,
      });
    });

    const order = Array.isArray(config.videoOrder) ? config.videoOrder : [];

    const orderedIds = [
      ...order.filter((id) => bySource.has(id)),
      ...Array.from(bySource.keys()).filter((id) => !order.includes(id)),
    ];

    return orderedIds.map((sourceId) => bySource.get(sourceId)!);
  }, [allEdges, allNodes, id, config.videoOrder]);

  const handleReorder = (sourceId: string, direction: 'up' | 'down') => {
    if (!connectedVideos.length) return;
    const index = connectedVideos.findIndex((v) => v.id === sourceId);
    if (index === -1) return;

    const newOrder = connectedVideos.map((v) => v.id);
    const swapWith = direction === 'up' ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= newOrder.length) return;

    const temp = newOrder[index];
    newOrder[index] = newOrder[swapWith];
    newOrder[swapWith] = temp;

    updateNodeData(id, {
      config: {
        ...(config || {}),
        videoOrder: newOrder,
      },
    });
  };

  return (
    <BaseNode
      title={
        (typeof (data as { label?: unknown })?.label === 'string' &&
          (data as { label?: string }).label) ||
        t('Merge Videos')
      }
      icon={
        <svg
          className='w-4 h-4 text-purple-600'
          fill='none'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path d='M4 4h6v6H4zM14 4h6v6h-6zM9 9h6v6H9zM4 14h6v6H4zM14 14h6v6h-6z' />
        </svg>
      }
      hasInput={false}
      hasOutput={false}
      status={((s) => {
        return typeof s === 'string' &&
          ['idle', 'running', 'success', 'error'].includes(s)
          ? (s as NodeExecutionStatus)
          : undefined;
      })((data as { status?: unknown })?.status)}
      selected={selected}
      nodeId={nodeId || id}
      onRunNode={onRunNode}
      customInputs={createInputHandle('video', 'video', 50)}
      customOutputs={createOutputHandle('video', 'video', 50)}
      hideToolbarActions
      customHeaderButtons={
        <>
          {videoUrl && (
            <SimpleTooltip
              title={showSettings ? t('Hide settings') : t('Show settings')}
              side='top'
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSettings(!showSettings);
                }}
                className='flex-shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors'
              >
                <svg
                  className='w-5 h-5 text-gray-600 dark:text-gray-300'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' />
                  <path d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                </svg>
              </button>
            </SimpleTooltip>
          )}
          <NodeActionsDropdown
            nodeId={nodeId || id}
            nodeTitle={(data.label as string) || t('Merge Videos')}
            mediaUrl={videoUrl}
            mediaType='video'
            filename='merged-video.mp4'
          />
        </>
      }
    >
      <div
        className={`space-y-3 ${showSettings || !videoUrl ? 'w-[400px]' : ''}`}
      >
        {showSettings && (
          <div className='space-y-2 px-4 py-3'>
            <p className='text-xs text-gray-600 dark:text-gray-300'>
              {t(
                'Connect two or more video outputs to merge them into a single video. Use the list below to control the order.',
              )}
            </p>
            {connectedVideos.length > 0 && (
              <div className='border border-gray-200 dark:border-gray-700 rounded-md p-2 bg-gray-50 dark:bg-gray-900/30'>
                <p className='text-[11px] font-medium text-gray-600 dark:text-gray-300 mb-1'>
                  {t('Merge order')}
                </p>
                <div className='space-y-1 overflow-auto'>
                  {connectedVideos.map((vid, index) => (
                    <div
                      key={vid.id}
                      className='flex items-center justify-between text-[11px] text-gray-700 dark:text-gray-200'
                    >
                      <span className='truncate mr-2'>
                        {index + 1}. {vid.label}
                      </span>
                      <span className='flex gap-0.5'>
                        <button
                          type='button'
                          onClick={() => handleReorder(vid.id, 'up')}
                          disabled={index === 0}
                          className='px-1 py-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-[10px]'
                          title={t('Move up')}
                        >
                          ↑
                        </button>
                        <button
                          type='button'
                          onClick={() => handleReorder(vid.id, 'down')}
                          disabled={index === connectedVideos.length - 1}
                          className='px-1 py-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 disabled:cursor-not-allowed text-[10px]'
                          title={t('Move down')}
                        >
                          ↓
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Run Button (inside settings) */}
        {showSettings && (
          <div className='px-4 pb-3'>
            <Button
              className='w-full justify-center'
              variant='outline'
              isLoading={isRunning}
              disabled={connectedVideos.length < 2}
              onClick={(e) => {
                e.stopPropagation();
                onRunNode?.(nodeId || id);
              }}
            >
              {videoUrl ? (
                <>
                  <RefreshIcon className='w-4 h-4 mr-2' />
                  <span>{t('Regenerate')}</span>
                </>
              ) : (
                <>
                  <PlayIcon className='w-4 h-4 mr-2' />
                  <span>{t('Run')}</span>
                </>
              )}
            </Button>
            {connectedVideos.length < 2 && (
              <p className='text-[10px] text-gray-500 text-center mt-1'>
                {t('Connect at least 2 videos to merge')}
              </p>
            )}
          </div>
        )}

        {shouldShowResult && (
          <div
            className={clsxm(
              showSettings
                ? 'w-full px-4 pb-3'
                : isLandscape
                  ? 'w-[700px]'
                  : 'w-[420px]',
            )}
          >
            <div className='relative group rounded-md overflow-hidden'>
              <video
                src={videoUrl}
                controls
                className='w-full h-auto object-cover'
                onLoadedMetadata={(e) => {
                  const video = e.currentTarget;
                  setIsLandscape(video.videoWidth > video.videoHeight);
                }}
                autoPlay={!isMobile}
                preload={isMobile ? 'metadata' : 'auto'}
                muted
                loop
                playsInline
              />
            </div>
          </div>
        )}

        {errorText && (
          <div className='border-t border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-2 py-2 rounded'>
            <p className='text-xs text-red-600 dark:text-red-400'>
              {errorText}
            </p>
          </div>
        )}
      </div>
    </BaseNode>
  );
}
